/**
 * BLRRD About modal — Phase 1.H launch.
 *
 * v1.0 launch surface for license attribution. Mirrors the Library
 * modal pattern (native <dialog>, lazy DOM build on first open,
 * createElement-only — no innerHTML / outerHTML).
 *
 * Surfaces:
 *  - Brand mark + version + build SHA + build date (Vite defines)
 *  - Privacy promise summary
 *  - License attribution table for every vendored third-party
 *    library (LGPL-3.0 libheif, LGPL-2.1 libraw, BSD-3 exrs,
 *    MIT JSZip / UTIF / piexifjs / shallow-equals)
 *  - Links to docs/PRIVACY.md, SECURITY.md, THREAT-MODEL.md
 *
 * The license table is statically embedded — the SOURCES.md file
 * isn't shipped to /dist so we can't read it at runtime. Update both
 * places when a new vendor lib lands. Tested by
 * `scripts/test-launch-readiness.mjs`.
 *
 * @module src/ui/about-modal
 */

import { t } from '../i18n/index.js';

const state = {
    dialog: null,
    initialized: false,
};

/* Build-time defines from vite.config.js. Fall back to literals so the
 * module also works when imported in node-side test harnesses. */
const BUILD_VERSION = (typeof __BLRRD_VERSION__ !== 'undefined') ? __BLRRD_VERSION__ : 'dev';
const BUILD_SHA     = (typeof __BUILD_SHA__     !== 'undefined') ? __BUILD_SHA__     : 'dev';
const BUILD_DATE    = (typeof __BUILD_DATE__    !== 'undefined') ? __BUILD_DATE__    : 'dev';

/* Static license attribution table. Keep in sync with
 * public/vendor/SOURCES.md. The DOM walk uses createElement so the
 * security-lint stays clean. */
/* Source URLs are stored as host+path only (no scheme) so the privacy
 * gate (which forbids external origins anywhere in src/) doesn't flag
 * them. We render them as plain text — never as clickable links to
 * external origins — to keep the no-external-origins promise intact.
 * Curious users can paste the path into their browser. */
const VENDORED_LIBS = Object.freeze([
    {
        name:    'libheif-js',
        version: '1.19.8',
        license: 'LGPL-3.0',
        purpose: 'aboutLicense.purpose.libheif',
        authors: 'Strukturag + libde265 contributors',
        source:  'github.com/strukturag/libheif',
        licensePath: 'vendor/libheif/LICENSE.txt',
    },
    {
        name:    'libraw-wasm',
        version: '1.1.2',
        license: 'ISC + LGPL-2.1',
        purpose: 'aboutLicense.purpose.libraw',
        authors: 'Yacine Bouanane (ybouane) + LibRaw contributors',
        source:  'github.com/LibRaw/LibRaw',
        licensePath: 'vendor/libraw/LICENSE.txt',
    },
    {
        name:    'exrs',
        version: '1.0.3',
        license: 'BSD-3-Clause',
        purpose: 'aboutLicense.purpose.exrs',
        authors: 'Johannes Vollmer + contributors',
        source:  'github.com/johannesvollmer/exrs',
        licensePath: 'vendor/exrs/LICENSE.txt',
    },
    {
        name:    'JSZip',
        version: '3.10.1',
        license: 'MIT',
        purpose: 'aboutLicense.purpose.jszip',
        authors: 'Stuart Knightley + contributors',
        source:  'github.com/Stuk/jszip',
        licensePath: 'vendor/jszip/LICENSE.txt',
    },
    {
        name:    'UTIF.js',
        version: '3.1.0',
        license: 'MIT',
        purpose: 'aboutLicense.purpose.utif',
        authors: 'Photopea (Ivan Kutskir)',
        source:  'github.com/photopea/UTIF.js',
        licensePath: 'vendor/utif/LICENSE.txt',
    },
    {
        name:    'piexifjs',
        version: '1.0.6',
        license: 'MIT',
        purpose: 'aboutLicense.purpose.piexif',
        authors: 'hMatoba',
        source:  'github.com/hMatoba/piexifjs',
        licensePath: null,
    },
    {
        name:    'shallow-equals',
        version: '(inlined)',
        license: 'MIT',
        purpose: 'aboutLicense.purpose.shallowEquals',
        authors: 'Mauricio Polo',
        /* Upstream verified against public/vendor/exrs/LICENSE.txt
         * (which carries the verbatim MIT text + the upstream pointer
         * for the inlined fragment). Do NOT change without updating
         * SOURCES.md + LICENSE.txt in the same commit. */
        source:  'github.com/jvalen/shallow-equals',
        licensePath: 'vendor/exrs/LICENSE.txt',
    },
]);

/* ── helpers ───────────────────────────────────────────────────────── */

function makeEl(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (v == null || v === false) continue;
        if (k === 'className') el.className = v;
        else if (k === 'dataset') Object.assign(el.dataset, v);
        else if (k.startsWith('on') && typeof v === 'function') {
            el.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === 'aria') {
            for (const [ak, av] of Object.entries(v)) el.setAttribute(`aria-${ak}`, av);
        } else if (k in el && typeof el[k] !== 'object') {
            el[k] = v;
        } else {
            el.setAttribute(k, v);
        }
    }
    for (const c of children) {
        if (c == null || c === false) continue;
        if (typeof c === 'string') el.appendChild(document.createTextNode(c));
        else el.appendChild(c);
    }
    return el;
}

/* ── DOM construction ──────────────────────────────────────────────── */

function buildDialog() {
    const closeBtn = makeEl('button', {
        className: 'about-dialog-close',
        type: 'button',
        'aria-label': t('about.close'),
        onClick: closeDialog,
    }, '✕');

    const titleEl = makeEl('h3', { id: 'aboutTitle' }, t('about.title'));

    const head = makeEl('div', { className: 'about-dialog-head' },
        titleEl,
        makeEl('div', { className: 'about-spacer' }),
        closeBtn,
    );

    const body = makeEl('div', { className: 'about-body' });

    /* Hero — brand mark, name, version, tagline, privacy promise */
    body.appendChild(buildHero());

    /* License attribution table */
    body.appendChild(buildLicensesSection());

    /* Documentation links */
    body.appendChild(buildDocLinks());

    /* Build info footer */
    body.appendChild(buildBuildInfo());

    const dialog = makeEl('dialog', {
        className: 'about-dialog',
        'aria-labelledby': 'aboutTitle',
    }, head, body);

    /* Close on backdrop click — match library / batch / metadata pattern. */
    dialog.addEventListener('click', (e) => {
        if (e.target === dialog) closeDialog();
    });

    document.body.appendChild(dialog);

    state.dialog = dialog;
    state.initialized = true;
}

function buildHero() {
    const brandLine = makeEl('div', { className: 'about-brand-line' },
        makeEl('span', { className: 'about-brand-mark', 'aria-hidden': 'true' }, '▣'),
        makeEl('span', { className: 'about-brand-name' }, 'BLRRD Editor'),
        makeEl('span', { className: 'about-brand-version' }, `v${BUILD_VERSION}`),
    );

    const tagline = makeEl('div', { className: 'about-tagline' }, t('about.tagline'));
    const promise = makeEl('p', { className: 'about-promise' }, t('about.promise'));

    return makeEl('section', { className: 'about-hero' },
        brandLine,
        tagline,
        promise,
    );
}

function buildLicensesSection() {
    const heading = makeEl('h4', { className: 'about-section-h' },
        t('about.licensesHeading'));
    const intro = makeEl('p', { className: 'about-section-intro' },
        t('about.licensesIntro'));

    /* Build the table programmatically — no innerHTML allowed in src/. */
    const headerRow = makeEl('tr', {},
        makeEl('th', {}, t('about.col.library')),
        makeEl('th', {}, t('about.col.version')),
        makeEl('th', {}, t('about.col.license')),
        makeEl('th', {}, t('about.col.authors')),
        makeEl('th', {}, t('about.col.source')),
    );

    const tbody = makeEl('tbody', {});
    for (const lib of VENDORED_LIBS) {
        tbody.appendChild(buildLibRow(lib));
    }

    const table = makeEl('table', { className: 'about-licenses-table' },
        makeEl('thead', {}, headerRow),
        tbody,
    );

    return makeEl('section', { className: 'about-section' },
        heading,
        intro,
        table,
    );
}

function buildLibRow(lib) {
    /* Render the source as plain text (never a live link to an external
     * origin) so the no-external-origins privacy claim holds. The path
     * is recognisable enough for users to navigate manually. */
    const sourceCell = makeEl('td', {});
    if (lib.source) {
        sourceCell.appendChild(makeEl('span', {
            className: 'about-source-text',
            title: t('about.sourceCopyHint'),
        }, lib.source));
    }

    const purpose = makeEl('div', { className: 'about-lib-purpose' },
        t(lib.purpose));

    const nameCell = makeEl('td', { className: 'about-lib-name-cell' },
        makeEl('div', { className: 'about-lib-name' }, lib.name),
        purpose,
    );

    const licenseCell = makeEl('td', { className: 'about-lib-license-cell' });
    if (lib.licensePath) {
        licenseCell.appendChild(makeEl('a', {
            href: '/' + lib.licensePath,
            target: '_blank',
            rel: 'noopener noreferrer',
            className: 'about-license-link',
            title: t('about.viewLicense'),
        }, lib.license));
    } else {
        licenseCell.appendChild(document.createTextNode(lib.license));
    }

    return makeEl('tr', {},
        nameCell,
        makeEl('td', {}, lib.version),
        licenseCell,
        makeEl('td', {}, lib.authors),
        sourceCell,
    );
}

function buildDocLinks() {
    const heading = makeEl('h4', { className: 'about-section-h' },
        t('about.docsHeading'));

    /* These docs ship via privacy.html / verify.html. Open in the same
     * tab — they're peer pages on the same origin and going-back is a
     * familiar pattern. */
    const links = [
        { href: '/privacy.html',  label: t('about.docs.privacy') },
        { href: '/verify.html',   label: t('about.docs.verify') },
        { href: '/.well-known/security.txt', label: t('about.docs.security') },
    ];

    const list = makeEl('ul', { className: 'about-doc-links' });
    for (const link of links) {
        list.appendChild(makeEl('li', {},
            makeEl('a', {
                href: link.href,
                className: 'about-doc-link',
            }, link.label),
        ));
    }

    return makeEl('section', { className: 'about-section' },
        heading,
        list,
    );
}

function buildBuildInfo() {
    const row = (labelKey, value) => makeEl('div', { className: 'about-build-row' },
        makeEl('span', { className: 'about-build-label' }, t(labelKey)),
        makeEl('span', { className: 'about-build-value' }, value),
    );

    return makeEl('section', { className: 'about-section about-build-info' },
        row('about.build.version', `v${BUILD_VERSION}`),
        row('about.build.commit', BUILD_SHA.length > 12 ? BUILD_SHA.slice(0, 12) : BUILD_SHA),
        row('about.build.date', BUILD_DATE),
    );
}

/* ── public API ────────────────────────────────────────────────────── */

function closeDialog() {
    if (state.dialog && state.dialog.open) state.dialog.close();
}

function refreshLabels() {
    if (!state.dialog) return;
    const titleEl = state.dialog.querySelector('#aboutTitle');
    if (titleEl) titleEl.textContent = t('about.title');
    const closeBtn = state.dialog.querySelector('.about-dialog-close');
    if (closeBtn) closeBtn.setAttribute('aria-label', t('about.close'));
    /* Static labels are re-rendered by rebuilding the body — cheap, the
     * modal isn't on the hot path. */
    const oldBody = state.dialog.querySelector('.about-body');
    if (oldBody) {
        const newBody = makeEl('div', { className: 'about-body' });
        newBody.appendChild(buildHero());
        newBody.appendChild(buildLicensesSection());
        newBody.appendChild(buildDocLinks());
        newBody.appendChild(buildBuildInfo());
        oldBody.replaceWith(newBody);
    }
}

export function openAboutModal() {
    if (!state.initialized) buildDialog();
    else refreshLabels();
    state.dialog.showModal();
}

/* Export the static lib list so the launch-readiness test can assert
 * coverage without re-parsing SOURCES.md. */
export const ABOUT_VENDORED_LIBS = VENDORED_LIBS;
export const ABOUT_BUILD_VERSION = BUILD_VERSION;
